import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../contexts/ApiContext';
import { applySchoolColors, defaultSchoolColors } from '../utils/schoolColors';
import {
  LayoutDashboard,
  FileText,
  CreditCard,
  User,
  LogOut,
  Menu,
  X,

  Bell,
  Settings,
  Palette
} from 'lucide-react';

const DashboardLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [, setSchoolColors] = useState(defaultSchoolColors);
  const { user, logout } = useAuth();
  const { api } = useApi();
  const location = useLocation();
  const navigate = useNavigate();

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Orders', href: '/orders', icon: FileText },
    { name: 'Payments', href: '/payments', icon: CreditCard },
    { name: 'Profile', href: '/profile', icon: User },
    { name: 'School Colors', href: '/school-colors', icon: Palette },
  ];

  const fetchSchoolColors = useCallback(async () => {
    try {
      const response = await api.get(`/users/${user.id}/school-colors`);
      if (response.data.colors) {
        setSchoolColors(response.data.colors);
        applySchoolColors(response.data.colors);
      }
    } catch (error) {
      // School colors endpoint not implemented yet - use defaults
      console.log('School colors not available, using defaults');
      applySchoolColors(defaultSchoolColors);
    }
  }, [api, user.id]);

  useEffect(() => {
    fetchSchoolColors();
  }, [fetchSchoolColors]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (href) => {
    return location.pathname === href;
  };

  return (
    <div className="min-h-screen bg-midnight">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-sky border-r-2 border-black transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between h-16 px-6 border-b-2 border-black">
          <div className="flex items-center">
            <img 
              src="/logo.png" 
              alt="BandCam.io Logo" 
              className="w-8 h-8 object-contain"
            />
            <span className="ml-3 text-xl font-bold text-black">BandCam.io</span>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-1 text-black hover:bg-goldie"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="mt-6 px-3">
          <div className="space-y-1">
            {navigation.map((item, index) => {
              const Icon = item.icon;
              const isEven = index % 2 === 0;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center px-3 py-2 text-sm font-medium border-2 transition-colors ${
                    isActive(item.href)
                      ? 'bg-black text-white border-black'
                      : isEven 
                        ? 'bg-sky text-black hover:bg-goldie border-transparent hover:border-black'
                        : 'bg-goldie text-black hover:bg-sky border-transparent hover:border-black'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <Icon
                    className={`mr-3 h-5 w-5 ${
                      isActive(item.href) ? 'text-white' : 'text-black group-hover:text-black'
                    }`}
                  />
                  {item.name}
                </Link>
              );
            })}
          </div>
        </nav>

        {/* User info and logout */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t-2 border-black bg-goldie">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-black rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-bold">
                  {user?.band_name?.charAt(0) || user?.email?.charAt(0) || 'U'}
                </span>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-black">
                  {user?.band_name || 'Band Director'}
                </p>
                <p className="text-xs text-gray-700">
                  {user?.school_name || 'School'}
                </p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="p-2 text-black hover:bg-sky border-2 border-transparent hover:border-black"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <div className="sticky top-0 z-30 bg-sky border-b-2 border-black">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 text-black hover:bg-goldie border-2 border-transparent hover:border-black"
            >
              <Menu className="w-6 h-6" />
            </button>
            
            <div className="flex items-center space-x-4">
              <button className="p-2 text-black hover:bg-goldie border-2 border-transparent hover:border-black">
                <Bell className="w-5 h-5" />
              </button>
              <button className="p-2 text-black hover:bg-goldie border-2 border-transparent hover:border-black">
                <Settings className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="p-4 sm:p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout; 